/**
 * <s-variant-picker> — Listens for option `change` events and swaps the product form
 * and title via partial rendering, keeping price and add-to-cart state in
 * sync with the selected variant without a full page reload.
 *
 * Required child markup:
 *   [data-variant-id]           — On each swatch/radio/select option input; value used to
 *                                 build the ?variant= URL param and trigger the partial fetch.
 *   select > option[data-variant-id] — Alternative: variant id carried on <option> elements
 *                                       inside a <select> (read via selectedOptions[0].dataset).
 *   input[name="id"]            — Hidden product-form input kept in sync for add-to-cart.
 *   input[name="quantity"]      — Quantity input preserved across product-form swaps.
 *
 * Element dataset (on <s-variant-picker>):
 *   data-updated-message   — i18n string announced via #sr-announcer after the variant updates.
 *   data-error-message     — i18n string shown locally and announced after a current update fails.
 *
 * Optional child markup:
 *   [data-variant-picker-error] — Visible error owned by the picker; add-to-cart errors remain
 *                                 owned by [data-add-to-cart-error].
 *
 * Partial contract:
 *   "product-form"   — must match {% partial "product-form" %} in templates/product.liquid.
 *   "product-title"  — must match {% partial "product-title" %} in templates/product.liquid.
 *                      Renaming either side silently breaks variant switching.
 *
 * External coupling:
 *   history.replaceState                  — ?variant= URL param updated on every selection.
 *   #sr-announcer (global)                — Live region announced with success/error feedback.
 *   aria-busy (on self)                   — Set while the current variant update is pending.
 */
// <s-variant-picker> listens for option changes and swaps the product form +
// title via partial rendering. This keeps price and the add-to-cart
// button in sync with the selected variant without a full page reload.
import { partials } from '@shopify/partial-rendering';
import { ProductSelectEvent } from '@shopify/standard-events';
import { announceGlobalLiveRegion } from '@theme/live-region';

const PRODUCT_VIEW_PAYLOAD_SELECTOR = 'script[type="application/json"][data-product-view-payload]';
const ACTIVE_UPDATE = Symbol('s-variant-picker-active-update');
// Latest visible selection the shopper made, kept on the stable product-form host because
// every variant refresh replaces the picker element itself.
const DESIRED_SELECTION = Symbol('s-variant-picker-desired-selection');
const POINTER_ACTIVE = Symbol('s-variant-picker-pointer-active');


function snapshotSelection(picker) {
  const selection = {};
  for (const select of picker.querySelectorAll('select[name]')) selection[select.name] = select.value;
  for (const radio of picker.querySelectorAll('input[type="radio"][name]:checked')) selection[radio.name] = radio.value;
  return selection;
}

function applySelection(picker, selection) {
  for (const [name, value] of Object.entries(selection)) {
    for (const radio of picker.querySelectorAll('input[type="radio"][name]')) {
      if (radio.name !== name) continue;
      radio.checked = radio.value === value;
      if (radio.checked) {
        const label = radio.closest('fieldset')?.querySelector('[data-selected-value-label]');
        if (label && radio.dataset.valueLabel) label.textContent = radio.dataset.valueLabel;
      }
    }
    for (const select of picker.querySelectorAll('select[name]')) {
      if (select.name === name && [...select.options].some((option) => option.value === value)) select.value = value;
    }
  }
}

function showUnavailable(picker, productForm) {
  const error = picker.querySelector('[data-variant-picker-error]');
  const message = productForm?.dataset.unavailableMessage;
  if (!error || !message) return;
  error.textContent = message;
  error.hidden = false;
  announceGlobalLiveRegion(message);
}

// Resolves once no pointer is pressed on the product form (capped so a stuck state never blocks).
function waitForPointerRelease(host) {
  if (!host?.[POINTER_ACTIVE]) return Promise.resolve();
  return new Promise((resolve) => {
    const done = () => {
      clearTimeout(timer);
      window.removeEventListener('pointerup', done, true);
      window.removeEventListener('pointercancel', done, true);
      // Let the click/change from this release run first so it can supersede the update.
      setTimeout(resolve, 0);
    };
    const timer = setTimeout(done, 800);
    window.addEventListener('pointerup', done, true);
    window.addEventListener('pointercancel', done, true);
  });
}

// Brings the selected variant's photo (e.g. the chosen color) to the front of the product gallery.
function showVariantMedia(root) {
  const mediaId = root?.querySelector('[data-variant-media-id]')?.dataset.variantMediaId;
  const gallery = document.querySelector('[data-product-gallery]');
  if (!mediaId || !gallery) return;
  const item = gallery.querySelector(`[data-media-id="${CSS.escape(mediaId)}"]`);
  if (!item) return;
  if (gallery.firstElementChild !== item) gallery.prepend(item);
  gallery.scrollTo({ left: 0, behavior: 'smooth' });
}

// Resolves the variant matching every currently visible option selection.
// Returns the variant id, null when the visible combination does not exist, or
// undefined when it cannot be determined (no map or picker on the page).
function resolveSelectedVariantId(productForm) {
  const mapEl = productForm?.querySelector('script[type="application/json"][data-product-variants]');
  const picker = productForm?.querySelector('s-variant-picker');
  if (!mapEl || !picker) return undefined;
  let data;
  try {
    data = JSON.parse(mapEl.textContent);
  } catch {
    return undefined;
  }
  const radios = [...picker.querySelectorAll('input[type="radio"][name]')];
  const selects = [...picker.querySelectorAll('select[name]')];
  const selected = data.options.map((name) => {
    const checked = radios.find((input) => input.name === name && input.checked);
    if (checked) return checked.value;
    const select = selects.find((el) => el.name === name);
    return select ? select.value : undefined;
  });
  if (selected.some((value) => value === undefined)) return undefined;
  const match = data.variants.find((variant) => variant.options.every((value, i) => value === selected[i]));
  return match ? String(match.id) : null;
}

class VariantPicker extends HTMLElement {
  #ac;
  #abortController;
  #committedHistoryState;
  #committedSelection;
  #committedUrl;
  #committedVariantId;

  connectedCallback() {
    this.#ac = new AbortController();
    const opts = { signal: this.#ac.signal };
    const productForm = this.closest('[data-product-form]');

    // The server-rendered form is the committed state to restore if its replacement fails.
    this.#committedVariantId = this.#getSelectedVariantId(productForm);
    showVariantMedia(productForm);
    this.#committedUrl = location.href;
    this.#committedHistoryState = history.state;
    this.#committedSelection = this.#captureSelection();

    // Track pressed pointers on the stable host so a refresh never lands mid-click.
    if (productForm) {
      productForm.addEventListener('pointerdown', () => { productForm[POINTER_ACTIVE] = true; }, opts);
      const release = () => { productForm[POINTER_ACTIVE] = false; };
      window.addEventListener('pointerup', release, opts);
      window.addEventListener('pointercancel', release, opts);
      if (!productForm[DESIRED_SELECTION]) productForm[DESIRED_SELECTION] = snapshotSelection(this);
    }

    this.addEventListener('change', (e) => {
      const input = e.target;
      // Record exactly what the shopper sees now; this is the selection that must survive.
      if (productForm && e.isTrusted) productForm[DESIRED_SELECTION] = snapshotSelection(this);
      // Per-button data-variant-id was rendered for the previous selection; after a quick
      // second click it can point at the old size. Prefer the full visible selection.
      const variantId = resolveSelectedVariantId(this.closest('[data-product-form]'))
        || input.dataset.variantId
        || input.selectedOptions?.[0]?.dataset?.variantId;
      if (variantId) {
        this.#update(variantId);
      }
    }, opts);

    // Initial load: an option tapped before this script ran changed the visible pills
    // but not the submitted id. Reconcile once per page so the cart gets what is shown.
    if (productForm && !productForm.hasAttribute('data-variant-reconciled')) {
      productForm.setAttribute('data-variant-reconciled', '');
      const visibleVariantId = resolveSelectedVariantId(productForm);
      if (visibleVariantId && visibleVariantId !== this.#committedVariantId) {
        this.#update(visibleVariantId);
      }
    }
  }

  disconnectedCallback() {
    this.#ac?.abort();
    this.#abortController?.abort();
  }

  async #update(variantId) {
    const productForm = this.closest('[data-product-form]');
    const updateHost = productForm || this;

    // The stable product-form host coordinates picker instances replaced by their own partial.
    // Refresh URL/history only when no optimistic update is active, never from a stale candidate.
    if (!updateHost[ACTIVE_UPDATE]) {
      this.#committedUrl = location.href;
      this.#committedHistoryState = history.state;
    }
    updateHost[ACTIVE_UPDATE]?.abort();
    this.#abortController?.abort();
    const controller = new AbortController();
    this.#abortController = controller;
    updateHost[ACTIVE_UPDATE] = controller;

    const quantityValue = this.#getQuantityValue(productForm);
    let focusCapture = null;
    this.#syncSelectedVariantId(variantId, productForm);
    this.#showError('');
    this.setAttribute('aria-busy', 'true');

    const deferredEvent = ProductSelectEvent.createPromise();
    this.dispatchEvent(
      new ProductSelectEvent({
        product: {
          id: this.dataset.productId || '',
          title: this.dataset.productTitle || '',
          handle: this.dataset.productHandle || '',
        },
        selectedOptions: this.#getSelectedOptions(),
        promise: deferredEvent.promise,
      })
    );

    const url = new URL(location.href);
    // ?variant is a Shopify platform contract — the server reads it to pre-select and render the
    // matching variant on page load / deep-link. Renaming this param breaks variant URL sharing.
    url.searchParams.set('variant', variantId);

    try {
      // Let the browser paint before URL update.
      await new Promise(resolve => setTimeout(resolve, 0));
      this.#throwIfSuperseded(controller, updateHost);
      history.replaceState(history.state, '', url);

      // 'product-form' and 'product-title' must match the {% partial %} names in
      // templates/product.liquid — changing either name here or there breaks variant switching.
      const html = await partials.fetch('product-form', 'product-title', { url: url.toString(), signal: controller.signal });
      this.#throwIfSuperseded(controller, updateHost);
      // Never swap the form under a pressed pointer: replacing the pill between press and
      // release swallows that click. A newer click supersedes this update while we wait.
      await waitForPointerRelease(updateHost);
      this.#throwIfSuperseded(controller, updateHost);
      // Applied directly, not inside a view transition: during a transition the page
      // ignores clicks (~250 ms), which dropped every other fast option change.
      focusCapture = this.#captureFocus();
      partials.apply(html);

      // Applying product-form disconnects this picker. Ownership, rather than its now-aborted
      // signal, distinguishes that expected replacement from a newer picker selection.
      if (updateHost[ACTIVE_UPDATE] !== controller) throw this.#abortError(controller);

      this.#syncSelectedVariantId(variantId, productForm);
      this.#syncQuantityValue(quantityValue, productForm);
      this.#restoreFocus(focusCapture, productForm);
      showVariantMedia(productForm);

      // The shopper's latest choice wins over whatever the server rendered. Re-check it in the
      // fresh picker; if it maps to another variant, fetch that one; if the combination does
      // not exist, keep it visible and show the notice (add to cart is blocked by the form).
      const freshPicker = productForm?.querySelector('s-variant-picker');
      const desired = updateHost[DESIRED_SELECTION];
      if (freshPicker && desired) {
        applySelection(freshPicker, desired);
        const desiredVariantId = resolveSelectedVariantId(productForm);
        if (desiredVariantId === null) {
          showUnavailable(freshPicker, productForm);
        } else if (desiredVariantId && desiredVariantId !== String(variantId)) {
          this.#syncSelectedVariantId(desiredVariantId, productForm);
          const trigger = freshPicker.querySelector('input[type="radio"]:checked, select[name]');
          trigger?.dispatchEvent(new Event('change', { bubbles: true }));
        }
      }

      deferredEvent.resolve({ variant: this.#getSelectedVariantPayload(productForm) });

      if (this.isConnected) this.#commitState(variantId, url);
      announceGlobalLiveRegion(this.dataset.updatedMessage);
    } catch (error) {
      deferredEvent.reject(error);

      const superseded = updateHost[ACTIVE_UPDATE] !== controller;
      if (superseded || !this.isConnected) return;

      this.#restoreCommittedState(productForm);
      if (productForm) productForm[DESIRED_SELECTION] = snapshotSelection(this);
      if (error?.name === 'AbortError') return;

      this.#showError(this.dataset.errorMessage);
      announceGlobalLiveRegion(this.dataset.errorMessage);
      console.error('[s-variant-picker] Partial fetch failed:', error);
    } finally {
      if (updateHost[ACTIVE_UPDATE] === controller) delete updateHost[ACTIVE_UPDATE];
      if (this.#abortController === controller) {
        this.#abortController = null;
        this.removeAttribute('aria-busy');
      }
    }
  }

  #getSelectedOptions() {
    return Array.from(
      this.querySelectorAll('select[name], input[type="radio"][name]:checked')
    ).map((control) => ({
      name: control.name,
      value: String(control.value ?? ''),
    }));
  }

  #captureSelection() {
    return Array.from(
      this.querySelectorAll('select[name], input[type="radio"][name]')
    ).map((control) => {
      if (control instanceof HTMLSelectElement) {
        return {
          control,
          selected: Array.from(control.options, (option) => option.selected),
        };
      }

      return { control, checked: control.checked };
    });
  }

  #restoreCommittedState(productForm) {
    for (const state of this.#committedSelection || []) {
      if (!this.contains(state.control)) continue;

      if (state.control instanceof HTMLSelectElement) {
        Array.from(state.control.options).forEach((option, index) => {
          option.selected = state.selected[index] || false;
        });
      } else {
        state.control.checked = state.checked;
      }
    }

    this.#syncSelectedVariantId(this.#committedVariantId, productForm);
    history.replaceState(this.#committedHistoryState, '', this.#committedUrl);
  }

  #commitState(variantId, url) {
    this.#committedVariantId = variantId;
    this.#committedUrl = url.toString();
    this.#committedHistoryState = history.state;
    this.#committedSelection = this.#captureSelection();
  }

  #captureFocus() {
    const activeElement = document.activeElement;
    if (!this.contains(activeElement)) return null;

    if (activeElement instanceof HTMLInputElement && activeElement.type === 'radio') {
      return {
        type: 'radio',
        name: activeElement.name,
        value: activeElement.value,
        control: activeElement,
      };
    }

    // Capture stable identity for select elements.
    if (activeElement instanceof HTMLSelectElement) {
      return {
        type: 'select',
        name: activeElement.name,
        control: activeElement,
      };
    }

    return null;
  }

  #restoreFocus(focusCapture, productForm = this.closest('[data-product-form]')) {
    if (!focusCapture || focusCapture.control.isConnected) return;
    if (document.activeElement !== document.body && document.activeElement !== document.documentElement) return;

    const newPicker = productForm?.querySelector('s-variant-picker');
    if (!newPicker) return;

    let candidates;
    if (focusCapture.type === 'radio') {
      candidates = newPicker.querySelectorAll('input[type="radio"][name]');
      candidates = [...candidates].filter((control) => (
        control.name === focusCapture.name && control.value === focusCapture.value
      ));
    } else {
      candidates = [...newPicker.querySelectorAll('select[name]')]
        .filter((control) => control.name === focusCapture.name);
    }

    candidates[0]?.focus();
  }

  #getSelectedVariantId(productForm = this.closest('[data-product-form]')) {
    const variantInput = productForm?.querySelector('input[name="id"]');
    return variantInput instanceof HTMLInputElement ? variantInput.value : '';
  }

  #syncSelectedVariantId(variantId, productForm = this.closest('[data-product-form]')) {
    const variantInput = productForm?.querySelector('input[name="id"]');
    if (variantInput instanceof HTMLInputElement) {
      variantInput.value = variantId;
    }
  }

  #showError(message) {
    const error = this.querySelector('[data-variant-picker-error]');
    if (!error) return;

    if (message) error.textContent = message;
    error.hidden = !message;
  }

  #throwIfSuperseded(controller, updateHost) {
    if (!controller.signal.aborted && updateHost[ACTIVE_UPDATE] === controller) return;
    throw this.#abortError(controller);
  }

  #abortError(controller) {
    const reason = controller.signal.reason;
    if (reason instanceof Error && reason.name === 'AbortError') return reason;
    return new DOMException('Variant update superseded', 'AbortError');
  }

  #getQuantityValue(productForm = this.closest('[data-product-form]')) {
    const quantityInput = productForm?.querySelector('input[name="quantity"]');
    return quantityInput instanceof HTMLInputElement ? quantityInput.value : null;
  }

  #syncQuantityValue(quantityValue, productForm = this.closest('[data-product-form]')) {
    if (!quantityValue) return;

    const quantityInput = productForm?.querySelector('input[name="quantity"]');
    if (quantityInput instanceof HTMLInputElement) {
      quantityInput.value = this.#clampQuantityValue(quantityValue, quantityInput);
      quantityInput.dispatchEvent(new Event('change', { bubbles: true }));
    }
  }

  #clampQuantityValue(quantityValue, quantityInput) {
    const parsedValue = Number(quantityValue);
    if (!Number.isFinite(parsedValue)) return quantityValue;

    let clampedValue = parsedValue;
    const min = Number(quantityInput.min);
    const max = Number(quantityInput.max);

    if (quantityInput.min && Number.isFinite(min)) {
      clampedValue = Math.max(min, clampedValue);
    }
    if (quantityInput.max && Number.isFinite(max)) {
      clampedValue = Math.min(max, clampedValue);
    }

    return String(clampedValue);
  }

  #getSelectedVariantPayload(productForm = this.closest('[data-product-form]')) {
    const marker = productForm?.querySelector(PRODUCT_VIEW_PAYLOAD_SELECTOR);
    const payload = marker?.textContent?.trim();
    if (!payload) return null;

    let parsedPayload;
    try {
      parsedPayload = JSON.parse(payload);
    } catch (error) {
      console.error('[variant-picker] Failed to parse product view payload:', error);
      return null;
    }

    return parsedPayload?.product?.selectedVariant || null;
  }
}
customElements.define('s-variant-picker', VariantPicker);
